import { useEffect, useState } from 'react';
import { Shell } from '../shell/Shell';
import { PageHeader, fmtDateTime } from '../atoms';
import { getUserEmail } from '../auth';
import { useToasts, Toasts } from '../Toasts';
import { computeChangedFields, isStaleWriteConflict } from '../optimisticConcurrency';
import { usePlanningPermissions } from '../usePlanningPermissions';
import { errMessage, runEvent, STALE_MESSAGE } from '../pageUtils';
import {
  listPlanningSettings, listAvailableBedrockModels, emitUpsertPlanningSettings, emitSetOsApiKey,
  SETTING_TEXT_KEYS, SETTING_MODEL_KEYS, SETTING_FLAG_KEYS, type PlanningSettings, type AvailableModel, type SettingKey,
} from '../api/planningSettings';

type AddToast = (type: 'success' | 'error' | 'info' | 'warn', message: string, persistent?: boolean) => string;

const TITLE = 'Shared defaults';

export function SettingsPage() {
  const { can, loading: permLoading } = usePlanningPermissions();
  const { toasts, addToast, removeToast } = useToasts();
  const canManage = can('planning-settings:manage');

  const [rows, setRows] = useState<PlanningSettings[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [models, setModels] = useState<AvailableModel[]>([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [modelsError, setModelsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    listPlanningSettings()
      .then(list => { if (!cancelled) setRows(list); })
      .catch((e: Error) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reloadTick]);

  useEffect(() => {
    let cancelled = false;
    setModelsLoading(true); setModelsError(null);
    listAvailableBedrockModels()
      .then(list => { if (!cancelled) setModels(list); })
      .catch((e: Error) => { if (!cancelled) setModelsError(e.message); })
      .finally(() => { if (!cancelled) setModelsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (!permLoading && !can('planning-settings:read')) {
    return (
      <Shell current="settings" crumbs={[{ label: 'Planning' }, { label: 'Settings' }]}>
        <div className="card">
          <div className="card-body" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)' }}>
            You don't have permission to view this page.
          </div>
        </div>
      </Shell>
    );
  }

  const globalRow = rows.find(r => r.scope === 'Global') ?? null;

  return (
    <Shell current="settings" crumbs={[{ label: 'Planning' }, { label: 'Settings' }]}>
      <PageHeader
        title="Settings"
        subtitle="The prompts and model used to summarise policy documents and assess planning applications."
      />
      {error ? (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--neg)', fontSize: 12.5 }}>
          Failed to load settings: {error}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <ScopeCard
            row={globalRow} loading={loading} canManage={canManage}
            models={models} modelsLoading={modelsLoading} modelsError={modelsError}
            onSaved={() => setReloadTick(t => t + 1)} addToast={addToast}
          />
        </div>
      )}
      <Toasts items={toasts} onRemove={removeToast} />
    </Shell>
  );
}

const ALL_KEYS: SettingKey[] = [...SETTING_TEXT_KEYS, ...SETTING_MODEL_KEYS, ...SETTING_FLAG_KEYS];
const HINT = { fontSize: 11.5, color: 'var(--ink-3)', marginBottom: 4 } as const;

function sectionHeading(step: string, title: string, blurb: string) {
  return (
    <div style={{ borderTop: '1px solid var(--line)', paddingTop: 14, marginTop: 6 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{step ? `${step}. ` : ''}{title}</div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{blurb}</div>
    </div>
  );
}

// The key is sent once and never read back: the field only ever shows its last four characters.
function OsKeyField({ hint, canManage, onSaved, addToast }: {
  hint: string | undefined;
  canManage: boolean;
  onSaved: () => void;
  addToast: AddToast;
}) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  async function save() {
    setSaving(true); setSaveErr(null);
    try {
      await runEvent(connId => emitSetOsApiKey(value.trim(), getUserEmail() ?? undefined, connId));
      setValue('');
      addToast('success', 'OS API key saved');
      onSaved();
    } catch (e) {
      setSaveErr(errMessage(e, 'Failed to save the OS API key'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="field">
      <label htmlFor="os-api-key">OS Data Hub API key</label>
      <div style={HINT}>
        {hint ? `A key ending …${hint} is set. ` : 'No key is set, so the MasterMap pass is skipped. '}
        Use a development-mode key for testing and a live project key in production. Stored encrypted; it cannot be read back.
      </div>
      {canManage && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            id="os-api-key" type="password" autoComplete="off" spellCheck={false} value={value}
            onChange={e => setValue(e.target.value)} placeholder={hint ? 'Paste a new key to replace it' : 'Paste the project key'}
            style={{ flex: 1, minWidth: 0 }}
          />
          <button className="btn" style={{ fontSize: 12, padding: '3px 10px' }} onClick={() => void save()} disabled={saving || !value.trim()}>
            {saving ? 'Saving…' : 'Save key'}
          </button>
        </div>
      )}
      {saveErr && <div style={{ color: 'var(--neg)', fontSize: 12.5 }}>{saveErr}</div>}
    </div>
  );
}

function draftFrom(row: PlanningSettings | null): Record<SettingKey, string> {
  return Object.fromEntries(ALL_KEYS.map(k => [k, row?.[k] ?? ''])) as Record<SettingKey, string>;
}

function ScopeCard({ row, loading, canManage, models, modelsLoading, modelsError, onSaved, addToast }: {
  row: PlanningSettings | null;
  loading: boolean;
  canManage: boolean;
  models: AvailableModel[];
  modelsLoading: boolean;
  modelsError: string | null;
  onSaved: () => void;
  addToast: AddToast;
}) {
  const [draft, setDraft] = useState<Record<SettingKey, string>>(() => draftFrom(null));
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  // Re-seed the drafts whenever the stored row changes (first load, or after a save/reload).
  useEffect(() => {
    setDraft(draftFrom(row));
  }, [row?.id, row?.updatedAt]);

  const dirty = ALL_KEYS.some(k => draft[k] !== (row?.[k] ?? ''));
  const setField = (k: SettingKey, v: string) => setDraft(d => ({ ...d, [k]: v }));

  async function save() {
    setSaving(true); setSaveErr(null);
    try {
      const payload: Record<string, unknown> = { ...draft };
      let expectedValues: Record<string, unknown> | undefined;
      // Only the changed settings are sent: the prompts together are larger than one event can carry.
      let changes: Partial<Record<SettingKey, string>> = draft;
      if (row) {
        const changed = computeChangedFields(row as unknown as Record<string, unknown>, payload);
        changes = Object.fromEntries(Object.keys(changed).map(k => [k, draft[k as SettingKey]]));
        expectedValues = {};
        for (const key of Object.keys(changed)) expectedValues[key] = (row as unknown as Record<string, unknown>)[key];
      }
      await runEvent(connId => emitUpsertPlanningSettings({
        scope: 'Global',
        ...changes,
        performedBy: getUserEmail() ?? undefined,
      }, connId, expectedValues));
      addToast('success', `${TITLE} saved`);
      onSaved();
    } catch (e) {
      if (e instanceof Error && isStaleWriteConflict(e.message)) { addToast('error', STALE_MESSAGE, true); onSaved(); }
      else setSaveErr(errMessage(e, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  }

  // A saved model that the account can no longer list must still show, or the select would silently change it.
  const modelSelect = (key: SettingKey, emptyLabel: string) => {
    const value = draft[key];
    const options = models.some(m => m.id === value) || !value ? models : [{ id: value, name: value }, ...models];
    return modelsError ? (
      <div style={{ color: 'var(--neg)', fontSize: 12.5 }}>Failed to load models: {modelsError}</div>
    ) : (
      <select value={value} onChange={e => setField(key, e.target.value)} disabled={!canManage || modelsLoading}>
        <option value="">{modelsLoading ? 'Loading models…' : emptyLabel}</option>
        {options.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
      </select>
    );
  };
  const textArea = (key: SettingKey, rows: number, placeholder: string) => (
    <textarea
      value={draft[key]} onChange={e => setField(key, e.target.value)} disabled={!canManage}
      rows={rows} style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }} placeholder={placeholder}
    />
  );

  return (
    <div className="card">
      <div className="card-hd">
        {TITLE}
        {canManage && (
          <div className="card-hd-actions">
            <button className="btn btn-primary" style={{ fontSize: 12, padding: '3px 10px' }} onClick={() => void save()} disabled={saving || loading || !dirty}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>
      <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>The prompts and model used for every assessment and policy summary.</div>
        {loading ? (
          <span className="skel" style={{ width: '100%', height: 160 }} />
        ) : (
          <>
            <div className="field">
              <label>Model</label>
              <div style={HINT}>Used by every call below unless that call has its own model set.</div>
              {modelSelect('modelId', 'Platform default')}
            </div>

            {sectionHeading('1', 'Policy summary', 'Runs when policy documents are summarised into a policy input.')}
            <div className="field">
              <label>Policy analysis prompt</label>
              {textArea('policyAnalysisPrompt', 14, 'Instructions for summarising a policy document set.')}
            </div>

            {sectionHeading('2', 'Locate site: clue extraction', 'Runs first on every Locate: reads the address, postcode and landmarks out of the case documents.')}
            <div className="field">
              <label>Clue extraction prompt</label>
              <div style={HINT}>Must return the JSON object the locator expects.</div>
              {textArea('clueExtractionPrompt', 14, 'Instructions for extracting location clues.')}
            </div>
            <div className="field">
              <label>Clue extraction model</label>
              <div style={HINT}>A small, cheap model is enough here: it only reads the first pages of each document.</div>
              {modelSelect('clueExtractionModelId', 'Same as the main model')}
            </div>

            {sectionHeading('3', 'Locate site: placement check', 'Runs after the boundary is traced, when the placement looks weak.')}
            <div className="field">
              <label>Placement check prompt</label>
              <div style={HINT}>A vision model compares the location plan with the map. Must return the JSON object the locator expects. Leave empty to skip the check.</div>
              {textArea('placementCheckPrompt', 14, 'Instructions for checking the placement of the site boundary.')}
            </div>
            <div className="field">
              <label>Placement check model</label>
              {modelSelect('placementCheckModelId', 'Same as the main model')}
            </div>

            {sectionHeading('', 'Locate site: OS MasterMap', 'A second matching pass for location plans the building match could not place with confidence. It matches the plan against OS MasterMap, the map most plans are printed from.')}
            <div className="field">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox" checked={draft.mastermapEnabled === 'true'} disabled={!canManage}
                  onChange={e => setField('mastermapEnabled', e.target.checked ? 'true' : '')}
                />
                Match against OS MasterMap when the building match is not confident
              </label>
              <div style={HINT}>Each pass uses about £0.20 of OS premium data, charged to the OS Data Hub project whose key is set below. Save to apply.</div>
            </div>
            <OsKeyField hint={row?.osApiKeyHint} canManage={canManage} onSaved={onSaved} addToast={addToast} />

            {sectionHeading('4', 'Assessment', 'Runs when a case is assessed. The three parts below are joined into one request.')}
            <div className="field">
              <label>Assessment opening line</label>
              <div style={HINT}>Placed before the application analysis prompt. {'{subject}'} is replaced with the application name.</div>
              {textArea('assessmentPreamble', 3, 'You are assessing planning risk for {subject}.')}
            </div>
            <div className="field">
              <label>Application analysis prompt</label>
              {textArea('applicationAnalysisPrompt', 14, 'Instructions for scoring a planning application against the policy inputs.')}
            </div>
            <div className="field">
              <label>Pre-application check prompt</label>
              <div style={HINT}>Used instead of the application analysis prompt when someone is thinking of applying and describes the proposal in their own words.</div>
              {textArea('preApplicationPrompt', 14, 'Instructions for checking a described proposal and advising the applicant.')}
            </div>
            <div className="field">
              <label>Site name instruction</label>
              <div style={HINT}>Added when the site has no confirmed name, so the assessment reports one on a final "SITE NAME:" line.</div>
              {textArea('siteNamePrompt', 5, 'Instruction asking for a final SITE NAME: line.')}
            </div>

            {sectionHeading('', 'Large documents', 'Used inside steps 1 and 4 when the documents are too large to process in one pass: each part is mapped, then the notes are reduced.')}
            <div className="field">
              <label>Large-document map prompt</label>
              <div style={HINT}>Used on each part. Placeholders: {'{brief}'} {'{part}'} {'{parts}'} {'{document}'}.</div>
              {textArea('chunkMapPrompt', 10, 'Instructions for extracting what matters from one part of a large document.')}
            </div>
            <div className="field">
              <label>Large-document map model</label>
              {modelSelect('chunkMapModelId', 'Same as the main model')}
            </div>
            <div className="field">
              <label>Large-document reduce prompt</label>
              <div style={HINT}>Combines the per-part notes into the final answer. Placeholder: {'{brief}'}.</div>
              {textArea('chunkReducePrompt', 8, 'Instructions for combining the per-part notes.')}
            </div>
            {saveErr && <div style={{ color: 'var(--neg)', fontSize: 12.5 }}>{saveErr}</div>}
            <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
              {row?.updatedAt ? `Last saved ${fmtDateTime(row.updatedAt)}` : 'Not set yet.'}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
